import { API_BASE_URL, apiFetch, tokenStore } from '@/lib/api'
import { useEffect, useRef, useState } from 'react'

// ── Types (mirror backend/scraping/api/serializers.py) ─────────────────────

export type Tier = 'http' | 'dynamic' | 'stealth' | 'vision'
export type Recipe = 'business' | 'menu' | 'products' | 'events'
export type Format =
  | 'markdown'
  | 'text'
  | 'html'
  | 'raw_html'
  | 'links'
  | 'metadata'
  | 'screenshot'
  | 'sections'
  | 'json'
export type JobKind = 'scrape' | 'crawl' | 'map' | 'extract'
export type JobStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface Section {
  id?: number
  kind: string
  heading: string
  text: string
  selector: string
  bbox: [number, number, number, number] | null
  confidence: number
  method: 'jsonld' | 'rules' | 'llm' | 'vision'
  order: number
}

export interface FieldProvenance {
  method: string
  source_url: string | null
  section?: number | null
  confidence: number
  alternatives?: {
    value: unknown
    method: string
    source_url: string
    confidence: number
  }[]
}

export interface ExtractionData {
  recipe: string
  data: Record<string, unknown>
  provenance: Record<string, FieldProvenance | Record<string, unknown>>
  confidence: number
  meets_required_fields: boolean
  error?: string
}

export interface PageResult {
  url: string
  final_url: string
  status_code: number | null
  tier: Tier | ''
  page_role: string
  duration_ms: number
  captcha: {
    kind: string
    solved: boolean
    solver?: string | null
    layers?: unknown[]
  } | null
  flags: string[]
  error: string
  error_code?: string
  markdown?: string
  text?: string
  html?: string
  raw_html?: string
  links?: { url: string; text: string }[]
  metadata?: Record<string, unknown>
  sections?: Section[]
  screenshot?: string | null
  json?: Record<string, ExtractionData>
  followed_pages?: string[]
}

export interface Job {
  id: string
  kind: JobKind
  url: string
  urls: string[]
  targets: Recipe[]
  options: Record<string, unknown>
  status: JobStatus
  origin: string
  created_by: string | null
  pages_total: number
  pages_done: number
  llm_tokens_in: number
  llm_tokens_out: number
  vision_pages: number
  captcha_solves: number
  heartbeat_at: string | null
  error: string
  created_at: string
  finished_at: string | null
}

export interface JobDetail extends Job {
  extractions: (ExtractionData & { id: number; page: number | null })[]
  result: (PageResult & { urls?: string[] }) | null
}

export interface StoredPage {
  id: number
  url: string
  final_url: string
  status_code: number | null
  tier: Tier | ''
  page_role: string
  markdown: string
  text: string
  links: { url: string; text: string }[]
  metadata: Record<string, unknown>
  screenshot_url: string | null
  captcha: PageResult['captcha']
  duration_ms: number
  flags: string[]
  error: string
  sections: Section[]
}

export interface Paginated<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface DomainProfile {
  id: number
  domain: string
  preferred_tier: 'http' | 'dynamic' | 'stealth'
  pinned: boolean
  do_not_scrape: boolean
  success_count: number
  block_count: number
  captcha_kinds: Record<string, number>
  min_delay_ms: number
  last_success_at: string | null
  last_block_at: string | null
}

export interface ProxyRow {
  id: number
  host_display: string
  source: 'manual' | 'free'
  enabled: boolean
  priority: number
  is_alive: boolean
  latency_ms: number | null
  consecutive_failures: number
  success_count: number
  fail_count: number
  last_checked_at: string | null
}

export interface ScraperSettings {
  captcha_provider: 'none' | 'capsolver' | 'twocaptcha'
  captcha_api_key_status: 'set' | 'unset'
  free_proxy_pool_enabled: boolean
  vision_auto_fallback: boolean
  daily_captcha_budget: number
  daily_vision_page_limit: number
  required_fields_per_recipe: Record<string, unknown>
  usage_today: { captcha: number; vision: number }
}

// ── Calls ──────────────────────────────────────────────────────────────────

const BASE = '/api/scraper'

export interface ScrapeResponse {
  job_id: string
  status: JobStatus
  error?: string
  data?: PageResult | null
}

export const scraperApi = {
  scrape: (body: Record<string, unknown>) =>
    apiFetch<ScrapeResponse>(`${BASE}/scrape/`, {
      method: 'POST',
      body: { ...body, origin: 'playground' }
    }),
  crawl: (body: Record<string, unknown>) =>
    apiFetch<{ job_id: string; status: JobStatus }>(`${BASE}/crawl/`, {
      method: 'POST',
      body: { ...body, origin: 'playground' }
    }),
  map: (body: Record<string, unknown>) =>
    apiFetch<{ job_id: string; status: JobStatus; urls?: string[] }>(
      `${BASE}/map/`,
      { method: 'POST', body: { ...body, origin: 'playground' } }
    ),
  extract: (body: Record<string, unknown>) =>
    apiFetch<{ job_id: string; status: JobStatus }>(`${BASE}/extract/`, {
      method: 'POST',
      body: { ...body, origin: 'playground' }
    }),
  jobs: (params = '') => apiFetch<Paginated<Job>>(`${BASE}/jobs/${params}`),
  job: (id: string) => apiFetch<JobDetail>(`${BASE}/jobs/${id}/`),
  pages: (id: string, page = 1) =>
    apiFetch<Paginated<StoredPage>>(`${BASE}/jobs/${id}/pages/?page=${page}`),
  cancel: (id: string) =>
    apiFetch<Job>(`${BASE}/jobs/${id}/cancel/`, { method: 'POST' }),
  domains: (q = '') =>
    apiFetch<Paginated<DomainProfile>>(
      `${BASE}/domains/?q=${encodeURIComponent(q)}`
    ),
  createDomain: (body: Partial<DomainProfile>) =>
    apiFetch<DomainProfile>(`${BASE}/domains/`, { method: 'POST', body }),
  updateDomain: (id: number, body: Partial<DomainProfile>) =>
    apiFetch<DomainProfile>(`${BASE}/domains/${id}/`, {
      method: 'PATCH',
      body
    }),
  proxies: (source = '') =>
    apiFetch<Paginated<ProxyRow>>(
      `${BASE}/proxies/${source ? `?source=${source}` : ''}`
    ),
  createProxy: (body: { url: string; priority?: number }) =>
    apiFetch<ProxyRow>(`${BASE}/proxies/`, { method: 'POST', body }),
  updateProxy: (id: number, body: Partial<ProxyRow> & { url?: string }) =>
    apiFetch<ProxyRow>(`${BASE}/proxies/${id}/`, { method: 'PATCH', body }),
  deleteProxy: (id: number) =>
    apiFetch<void>(`${BASE}/proxies/${id}/`, { method: 'DELETE' }),
  checkProxy: (id: number) =>
    apiFetch<{ is_alive: boolean; latency_ms: number | null }>(
      `${BASE}/proxies/${id}/check/`,
      { method: 'POST' }
    ),
  settings: () => apiFetch<ScraperSettings>(`${BASE}/settings/`),
  updateSettings: (
    body: Partial<ScraperSettings> & { captcha_api_key?: string }
  ) => apiFetch<ScraperSettings>(`${BASE}/settings/`, { method: 'PATCH', body })
}

export const FINAL_STATUSES: JobStatus[] = ['completed', 'failed', 'cancelled']

/** Screenshots need the staff Bearer token, so load them as a blob URL. */
export async function fetchScreenshot(path: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${tokenStore.getAccess() ?? ''}` }
  })
  if (!res.ok) throw new Error(`Screenshot failed (${res.status})`)
  return URL.createObjectURL(await res.blob())
}

// ── Live progress (ws/scraper/jobs/<id>/) ──────────────────────────────────

export interface JobEvent {
  event:
    | 'tier_escalated'
    | 'captcha'
    | 'page_done'
    | 'section_found'
    | 'extraction_ready'
    | 'job_done'
    | string
  payload: Record<string, unknown>
  at?: string
}

function wsBase(): string {
  const explicit =
    (import.meta.env.VITE_WS_BASE_URL as string | undefined) ?? ''
  if (explicit) return explicit.replace(/\/$/, '')
  return API_BASE_URL.replace(/^http/, 'ws').replace(/\/$/, '')
}

/** Streams a job's events; falls back to nothing if the socket can't open
 *  (callers also poll GET jobs/{id}, so progress still shows). */
export function useJobEvents(jobId: string | null, max = 200): JobEvent[] {
  const [events, setEvents] = useState<JobEvent[]>([])
  const socketRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    setEvents([])
    if (!jobId) return
    const token = tokenStore.getAccess()
    if (!token) return
    let socket: WebSocket
    try {
      socket = new WebSocket(`${wsBase()}/ws/scraper/jobs/${jobId}/`, [
        'jwt',
        token
      ])
    } catch {
      return
    }
    socketRef.current = socket
    socket.onmessage = (message) => {
      try {
        const event = JSON.parse(message.data as string) as JobEvent
        setEvents((prev) => [event, ...prev].slice(0, max))
      } catch {
        // ignore malformed frames
      }
    }
    return () => {
      socket.close()
      socketRef.current = null
    }
  }, [jobId, max])

  return events
}
